// src/scene/OceanConfig.ts
// Ocean visual configuration — generated by Debug.
// Paste this entire file to replace src/scene/OceanConfig.ts

// ── Foam ─────────────────────────────────────────────────────────────────────
export const foamCenterOffset = { x: -0.0100, y: -0.0700 }; // XZ nudge on top of the auto-baked island center
export const foamRadius       = 0.9400; // >1 pushes ring outward, <1 pulls inward
export const foamWidth        = 0.1610; // width of the foam band
export const foamIntensity    = 0.0800; // overall foam brightness
export const foamAnimSpeed    = 0.4500; // speed of animated shimmer
export const foamEdgeNoiseAmt = 0.1200; // brightness shimmer near edge
export const foamWobbleAmt    = 0.1500; // world-unit wobble of the foam line
export const foamWobbleFreq   = 4.1000; // spatial frequency of the wobble
export const foamWobbleSpeed  = 0.2700; // how fast the wobble animates
export const foamLineFrequency = 13.0000;
export const foamLineThickness = 0.0270;
export const foamLineCount     = 1.0000;
export const foamLineBreakup   = 0.0000;
export const foamLineColor     = { r: 1.0000, g: 0.9200, b: 1.0000 };

// ── Ocean Waves (normal-map scroll) ──────────────────────────────────────────
export const normalMapScale    = 0.1160;
export const normalMapStrength = 0.1400;
export const waveVelocity1     = { x: 0.0330, y: -0.0100 };
export const waveVelocity2     = { x: -0.0270, y: -0.0180 };
export const edgeFadeDistance  = 0.0600;

// Horizon haze, driven by the view's GRAZING ANGLE (not distance). The ocean
// plane edge and any grazing-angle reflection speck always sit where the line of
// sight is near-horizontal, so veil that band by blending the surface to the
// sky's horizon color there. Distance-based fog could never catch it because the
// horizon's distance changes with camera height; the grazing angle does not.
// Values are -viewDir.y (0 = looking dead horizontal at the horizon, ~1 = down).
// Full haze at the horizon, fading to none once the view tips below ~End.
export const horizonFadeStart  = 0.0000;  // -viewDir.y where the haze starts fading out
export const horizonFadeEnd    = 0.0180;  // -viewDir.y where the haze is fully gone (looking down)
// Dialed to 10% of the original 0.18 reach — this haze was a band-aid for a
// grazing-angle reflection artifact that has since been fixed at the source.
// Kept at a sliver rather than removed in case any residual edge case reappears.

// ── Surface Vertex Displacement (near-camera swell) ──────────────────────────
// Real geometry waves applied ONLY to the strip of ocean in front of the camera
// — amplitude fades to 0 with distance so it fuses seamlessly with the flat
// surface. Gives the waterline a wavy silhouette when entering/exiting the water.
export const surfaceWaveAmplitude   = 0.0500; // max vertical displacement (world units) near the camera
export const surfaceWaveLength      = 3.7000; // wavelength in world units (keep ≥ ~4× vertex spacing)
export const surfaceWaveSpeed       = 0.6000; // animation speed of the swell
export const surfaceWaveRange       = 18.0000; // XZ distance from camera over which the waves fade to flat
export const surfaceWaveForwardBias = 0.6000; // 0 = full radial ring around camera, 1 = only directly ahead
export const surfaceWaveSteepness   = 0.2000; // blend of the cross wave layer — adds choppiness
// The swell is camera-relative (follows the camera, biased ahead of its
// heading) and always active, regardless of camera height — it used to fade
// out above water (kept only near the crossing) to hide a horizon-reflection
// speck at the pug zoom's height, but suppressing the ripple isn't the right
// tool for that anymore now that it's meant to always be visible. Steepness
// was dialed back instead: the ocean surface doesn't write depth (so it
// never occludes underwater content drawn after it — see OceanMaterial.ts),
// which means when the displaced surface folds over itself in screen space
// at a grazing angle, there's no z-buffer to resolve which fold is nearest —
// triangles just draw in mesh order, occasionally showing the wrong (farther)
// fold, including gaps straight through to the sky. Less steepness means a
// gentler slope, which folds far less readily at the angles this camera
// actually uses. Not a complete fix (still possible at extreme angles), but
// a real depth-buffer fix risks breaking fish/bubble occlusion against rocks
// elsewhere and needs visual verification this environment can't do.

// ── Ocean Surface ─────────────────────────────────────────────────────────────
export const surfaceColor   = { r: 0.0000, g: 1.1800, b: 1.1700 }; // RGB tint (1,1,1 = no tint)
export const surfaceOpacity = 0.8500;
export const waterBlurStrength = 0.0240;
export const waterBlurRadius   = 3.0000;
export const waterBlurOpacity  = 0.3500;
export const waterlineCompositeOpacity = 0.1800;

// ── Waterline (Y is used by apple buoyancy physics) ──────────────────────────
export const waterlineY         = 0.0050;

// ── Edge-foam (ocean-side depth intersection foam) ──────────────────────────
// Industry-standard "intersection foam": the ocean shader reads the opaque
// scene depth and brightens fragments where the ocean grazes geometry behind
// it. Fully independent from the SDF foam ring around the island silhouette.
export const edgeFoamWidth     = 0.1400;  // world-space distance over which foam fades to nothing
export const edgeFoamIntensity = 0.4200;  // overall brightness of the depth-intersection foam
export const edgeFoamUnderwaterMul = 0.3500;  // dimming factor for the same effect with camera below water (silvery refraction sheen)
export const edgeFoamColor     = { r: 1.0000, g: 0.9700, b: 1.0000 };

// World-Z fade for edge foam. Fade starts at the pug's world Z (-2.3) where
// precision is clean, gone by edgeFoamFadeEndZMobile. Desktop is off-scene.
// Pug world Z = islandPosition.z (-3.3) + pugOffset.z (+1.0) = -2.3
// (The real iOS flicker source turned out to be specific unexcluded objects
// in the depth pre-pass — chest ray planes, fish/jellyfish/particles, the
// campfire flame sprite — now fixed at the source in Scene.ts's
// getDepthPrePassExcluded(). This fade is a looser safety margin now, not a
// load-bearing fix.)
export const edgeFoamFadeStartZDesktop = -40.0000;
export const edgeFoamFadeEndZDesktop   = -60.0000;
export const edgeFoamFadeStartZMobile  = -1.5000;
export const edgeFoamFadeEndZMobile    = -8.2500;

// ── Reflection ────────────────────────────────────────────────────────────────
export const reflectionFresnelPower = 0.6500; // lower = visible at more angles
export const reflectionFloor        = 0.2200; // minimum reflectivity at any angle
export const skyReflectionBrightness = 1.0000; // scales the analytical skybox reflection (0 = none, 1 = full)
export const skyReflFalloff         = 6.5000; // sharpens near→far gradient: 1 = linear, 2 = squared, 4 = very steep

// ── Underwater ────────────────────────────────────────────────────────────────
export const oceanAbsorption    = { r: 0.1290, g: 0.0440, b: 0.0350 }; // per-channel fog depth
export const underwaterFogDist  = 80.0000;                                  // far distance — objects beyond this are fully fogged
export const distortionStrength = 0.0115;
export const distortionSpeed    = 0.6200;
export const distortionScale    = 8.5000;
export const distortionEdgeFade = 0.0600;

// ── Underwater "over/under" tint (screen-space line) ────────────────────────
// The effect tints everything BELOW a wavy line — the ocean surface projected
// uLineDistance ahead of the level scroll camera (PostProcess.ts). The ripple
// reuses the real surface wave shape (length/speed/direction) so it stays in
// phase with the rendered water. Purely 2D — no depth/geometry. Values below
// were tuned on the preview via the "Underwater Line" Debug GUI.
export const underwaterTintColor        = { r: 0.0400, g: 0.1200, b: 0.2600 }; // single navy tone mixed in underwater
export const underwaterTintStrength     = 0.3500; // mix amount below the line (uniform — no depth gradient)
export const underwaterLineDistance     = 1.9000; // world distance the line is projected ahead — smaller = line sits lower/off-screen when above water (0% at the top of the scroll on both FOVs), 50% at the waterline crossing
export const underwaterLineHeightOffset = 0.0400; // extra NDC nudge of the line up/down
export const underwaterLineWobbleGain   = 0.0550; // NDC amplitude of the line's ripple (how much it undulates)
export const underwaterLineEdge         = 0.2000; // NDC softness of the boundary (smoothstep half-width)

// ── Fish / Jellyfish Lighting ───────────────────────────────────────────────
// Drives the per-jellyfish PointLight (candela-ish intensity + reach in world
// units). Material recompiles are avoided by keeping light *count* constant —
// only the values below change at runtime.
export const jellyfishLightConfig = {
    intensity: 3.0000,
    distance: 1.5000,
};

// ── Background Whale ────────────────────────────────────────────────────────
// The whale far out in the murk. Everything here is read live each frame so the
// Debug GUI can drive it.
//
// The three that interact: pushing `z` further back deepens the fog but also
// shrinks the whale (raise `scale` to compensate), and `speed` should scale
// with the depth to keep the same APPARENT pace on screen — at 3× the distance
// a whale needs 3× the world speed to look like it's moving the same.
//
// `fogDistance` is the whale's OWN copy of the underwater fog far-distance, not
// the scene's (which is 80 and is shared by the sea floor). Lowering it is the
// direct control for "I want it more washed out": the shader fogs by
// viewDistance/fogDistance, so once fogDistance drops near the whale's actual
// distance from the camera the body is fully replaced by the water colour.
export const whaleConfig = {
    y:             -7.8000,
    z:            -32.0000,
    scale:          1.4000,
    speed:          0.5500,   // world units/s
    bobAmplitude:   0.3500,
    bobSpeed:       0.3500,   // rad/s
    fogDistance:   38.0000,   // lower = more fogged; scene default is 80
    dayOpacity:     0.5000,   // on top of the fog, at full day
    nightOpacity:   0.0700,   // "almost invisible" at full night
};

// ── Click Ripple Effect ───────────────────────────────────────────────────────
export const rippleSpeed           = 0.7;    // World-units/sec the ring expands (max radius = speed × lifetime)
export const rippleLifetime        = 1;    // Seconds before the wave fully fades out
export const rippleWidth           = 0.12;   // Width of the wave band in world units
export const rippleNormalStrength  = 1.2;    // Normal-perturbation amplitude — keep in line with wave normals
export const rippleMaxClickDistance = 70;  // Max XZ distance (world units) from camera for the effect to trigger